import { CART_STATUS_CODES } from "@swiggy-private/cart-types";
import { format, isEmpty } from "@swiggy-private/js-utils";
import { ErrorWidgetAttr } from "../interfaces";
import { ERROR_WIDGET_ATTR } from "@core-ui/constants/Error";
import type { ErrorWidgetProps } from "@core-ui/components/cart-footer-error-widget/ErrorWidget";
import type { CLICKED_CTA } from "@core-ui/components/cart-footer-error-widget/constants";

export interface ErrorWidgetEventPayload {
    statusCode: number;
    statusTitle: string;
    statusMessage: string;
    ctaText: string;
    secondaryCtaText: string;
    ctaAction: string;
    secondaryCtaAction: string;
}

// Builds the analytics payload for the error half-card.
// When `clickedCta` is provided, only the clicked CTA's action is populated so
// ARD can unambiguously tell which button the user pressed.
export const buildErrorHalfCardEventPayload = (props: ErrorWidgetProps, clickedCtaType?: CLICKED_CTA) => {
    return {
        statusCode: props.statusCode || 0,
        statusTitle: props.titleMessage || "",
        statusMessage: props.statusMessage || "",
        ctaText: props.ctaText || "",
        secondaryCtaText: props.secondaryCtaText || "",
        ctaAction: props.ctaAction || "",
        secondaryCtaAction: props.secondaryCtaAction || "",
        clickedCtaType,
    };
};

export const getErrorWidgetAttrs = ({
    statusCode,
    ctaIcon,
    ctaText,
    secondaryCtaText,
    statusMessage,
    titleMessage,
}: {
    statusCode: CART_STATUS_CODES;
    titleMessage?: string;
    statusMessage?: string;
    ctaText?: string;
    ctaIcon?: string;
    secondaryCtaText?: string;
}): ErrorWidgetAttr => {
    let defaultVal;
    switch (statusCode) {
        case CART_STATUS_CODES.LOCATION_UNSERVICEABLE:
            defaultVal = ERROR_WIDGET_ATTR.LOCATION_UNSERVICEABLE;
            break;
        case CART_STATUS_CODES.RESTAURANT_CLOSED:
            defaultVal = ERROR_WIDGET_ATTR.RESTAURANT_CLOSED;
            break;
        case CART_STATUS_CODES.RESTAURANT_CLOSED_WITH_REOPEN_TODAY:
            defaultVal = ERROR_WIDGET_ATTR.RESTAURANT_CLOSED_WITH_REOPEN_TODAY;
            break;
        case CART_STATUS_CODES.RESTAURANT_CLOSED_WITH_REOPEN_TOMORROW:
            defaultVal = ERROR_WIDGET_ATTR.RESTAURANT_CLOSED_WITH_REOPEN_TOMORROW;
            break;
        case CART_STATUS_CODES.RESTAURANT_CLOSED_WITH_REOPEN_SPECIFIC_DAY:
            defaultVal = ERROR_WIDGET_ATTR.RESTAURANT_CLOSED_WITH_REOPEN_SPECIFIC_DAY;
            break;
        case CART_STATUS_CODES.RESTAURANT_CLOSED_WITH_REOPEN_SPECIFIC_DATE:
            defaultVal = ERROR_WIDGET_ATTR.RESTAURANT_CLOSED_WITH_REOPEN_SPECIFIC_DATE;
            break;
        case CART_STATUS_CODES.RESTAURANT_CLOSED_WITH_REOPEN_IN_MINUTES:
            defaultVal = ERROR_WIDGET_ATTR.RESTAURANT_CLOSED_WITH_REOPEN_IN_MINUTES;
            break;
        case CART_STATUS_CODES.RESTAURANT_CLOSED_WITH_NO_INFO:
            defaultVal = ERROR_WIDGET_ATTR.RESTAURANT_CLOSED_WITH_NO_INFO;
            break;
        case CART_STATUS_CODES.CITY_CLOSED:
            defaultVal = ERROR_WIDGET_ATTR.CITY_CLOSED;
            break;
        case CART_STATUS_CODES.ITEM_OUTOFSTOCK:
            defaultVal = ERROR_WIDGET_ATTR.ITEM_OUTOFSTOCK;
            break;
        case CART_STATUS_CODES.RESTAURANT_UNSERVICEABLE:
            defaultVal = ERROR_WIDGET_ATTR.RESTAURANT_UNSERVICEABLE;
            break;
        case CART_STATUS_CODES.TOO_MANY_ITEMS:
            defaultVal = ERROR_WIDGET_ATTR.TOO_MANY_ITEMS;
            break;
        case CART_STATUS_CODES.EXCEEDED_INVENTORY:
            defaultVal = ERROR_WIDGET_ATTR.EXCEEDED_INVENTORY;
            break;
        case CART_STATUS_CODES.PREORDER_SLOT_EXPIRED_CART_ERROR:
            defaultVal = ERROR_WIDGET_ATTR.PREORDER_SLOT_EXPIRED_CART_ERROR;
            break;
        case CART_STATUS_CODES.SUPER_SUBSCRIPTION_INVALID:
            defaultVal = ERROR_WIDGET_ATTR.SUPER_SUBSCRIPTION_INVALID;
            break;
        case CART_STATUS_CODES.MIN_CART_VALUE_ERROR:
            defaultVal = ERROR_WIDGET_ATTR.MIN_CART_VALUE_ERROR;
            break;
        case CART_STATUS_CODES.EXTERNAL_VALIDATION_FAILED_CART_ERROR:
            defaultVal = ERROR_WIDGET_ATTR.EXTERNAL_VALIDATION_FAILED_CART_ERROR;
            break;
        case CART_STATUS_CODES.INCREASE_IN_DEMAND:
            defaultVal = ERROR_WIDGET_ATTR.INCREASE_IN_DEMAND;
            break;
        case CART_STATUS_CODES.RAINS:
            defaultVal = ERROR_WIDGET_ATTR.RAINS;
            break;
        case CART_STATUS_CODES.ZONE_NOT_OPEN:
            defaultVal = ERROR_WIDGET_ATTR.ZONE_NOT_OPEN;
            break;
        case CART_STATUS_CODES.BLACKZONE:
            defaultVal = ERROR_WIDGET_ATTR.BLACKZONE;
            break;
        case CART_STATUS_CODES.TOO_MANY_ITEMS_ON_CART:
            defaultVal = ERROR_WIDGET_ATTR.TOO_MANY_ITEMS_ON_CART;
            break;
        case CART_STATUS_CODES.BREAKFAST_SLOT_UNAVAILABLE:
            defaultVal = ERROR_WIDGET_ATTR.BREAKFAST_SLOT_UNAVAILABLE;
            break;
        case CART_STATUS_CODES.SELECTED_SLOT_UNAVAILABLE:
            defaultVal = ERROR_WIDGET_ATTR.SELECTED_SLOT_UNAVAILABLE;
            break;
        case CART_STATUS_CODES.BULK_ORDER_TOTAL_PRICE_IS_BELOW_THRESHOLD:
            defaultVal = ERROR_WIDGET_ATTR.BULK_ORDER_TOTAL_PRICE_IS_BELOW_THRESHOLD;
            break;
        case CART_STATUS_CODES.GIFTING_FOOD_CART_FAILURE:
            defaultVal = ERROR_WIDGET_ATTR.GIFTING_FOOD_CART_FAILURE;
            break;
        case CART_STATUS_CODES.GIFTING_FOOD_CART_POST_ORDER_FAILURE:
            defaultVal = ERROR_WIDGET_ATTR.GIFTING_FOOD_CART_POST_ORDER_FAILURE;
            break;
        // Instamart specific error handling
        case CART_STATUS_CODES.IM_CART_FAILURE:
            defaultVal = ERROR_WIDGET_ATTR.IM_CART_FAILURE;
            break;
        case CART_STATUS_CODES.INSTAMART_ITEM_LEVEL_LIMIT_EXCEEDED:
            defaultVal = ERROR_WIDGET_ATTR.INSTAMART_ITEM_LEVEL_LIMIT_EXCEEDED;
            break;
        case CART_STATUS_CODES.INSTAMART_TOTAL_ITEM_LIMIT_EXCEEDED:
            defaultVal = ERROR_WIDGET_ATTR.INSTAMART_TOTAL_ITEM_LIMIT_EXCEEDED;
            break;
        case CART_STATUS_CODES.INSTAMART_TOTAL_WEIGHT_LIMIT_EXCEEDED:
            defaultVal = ERROR_WIDGET_ATTR.INSTAMART_TOTAL_WEIGHT_LIMIT_EXCEEDED;
            break;
        case CART_STATUS_CODES.INSTAMART_TOTAL_VOLUME_LIMIT_EXCEEDED:
            defaultVal = ERROR_WIDGET_ATTR.INSTAMART_TOTAL_VOLUME_LIMIT_EXCEEDED;
            break;

        // IRCTC NON PNR FLOW
        case CART_STATUS_CODES.IRCTC_INVALID_PNR:
            defaultVal = ERROR_WIDGET_ATTR.IRCTC_INVALID_PNR;
            break;
        case CART_STATUS_CODES.IRCTC_SEAT_NOT_CONFIRMED:
            defaultVal = ERROR_WIDGET_ATTR.IRCTC_SEAT_NOT_CONFIRMED;
            break;
        case CART_STATUS_CODES.IRCTC_API_FAILURE:
            defaultVal = ERROR_WIDGET_ATTR.IRCTC_API_FAILURE;
            break;
        case CART_STATUS_CODES.IRCTC_CUT_OFF_TIME_EXPIRED:
            defaultVal = ERROR_WIDGET_ATTR.IRCTC_CUT_OFF_TIME_EXPIRED;
            break;
        case CART_STATUS_CODES.IRCTC_STATION_NOT_IN_JOURNEY:
            defaultVal = ERROR_WIDGET_ATTR.IRCTC_STATION_NOT_IN_JOURNEY;
            break;
        case CART_STATUS_CODES.IRCTC_ORDER_WINDOW_LIMIT_ERROR:
            defaultVal = ERROR_WIDGET_ATTR.IRCTC_ORDER_WINDOW_LIMIT_ERROR;
            break;

        default:
            defaultVal = ERROR_WIDGET_ATTR.DEFAULT;
            break;
    }

    return {
        cta: ctaText || defaultVal.cta,
        icon: ctaIcon || defaultVal.icon,
        headerIcon: defaultVal.headerIcon,
        message: statusMessage || defaultVal.message,
        title: titleMessage || defaultVal.title,
        showOverlay: defaultVal?.showOverlay ?? false,
        secondaryCtaText: secondaryCtaText || defaultVal.secondaryCtaText,
        genericIcon: defaultVal.genericIcon,
    };
};

// replace the {0} with the provided value in the message
export const getErrorWidgetMessage = (
    message: string,
    statusCode: CART_STATUS_CODES,
    errorData?: {
        maxAllowedCartWeightInGrams?: number;
        maxAllowedCartVolumeInCc?: number;
        restaurantName?: string;
        statusMessage?: string;
    },
) => {
    if (isEmpty(errorData)) {
        return message;
    }

    switch (statusCode) {
        case CART_STATUS_CODES.INSTAMART_TOTAL_WEIGHT_LIMIT_EXCEEDED:
            if (!isEmpty(errorData.maxAllowedCartWeightInGrams)) {
                return format(message, (errorData.maxAllowedCartWeightInGrams / 1000).toString());
            }
            return message;
        case CART_STATUS_CODES.INSTAMART_TOTAL_VOLUME_LIMIT_EXCEEDED:
            if (!isEmpty(errorData.maxAllowedCartVolumeInCc)) {
                return format(message, (errorData.maxAllowedCartVolumeInCc / 1000).toString());
            }
            return message;
        case CART_STATUS_CODES.GIFTING_FOOD_CART_FAILURE:
            if (!isEmpty(errorData.restaurantName)) {
                return format(message, errorData.restaurantName);
            }
            return message;
        case CART_STATUS_CODES.GIFTING_FOOD_CART_POST_ORDER_FAILURE:
            if (!isEmpty(errorData?.statusMessage)) {
                return errorData.statusMessage;
            }
            return message;
        default:
            return message;
    }
};
